import { Typography, Box, Card } from "@mui/material";
import DataTable from "examples/Tables/DataTable";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import BlockIcon from "@mui/icons-material/Block";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useEffect, useState } from "react";
import {
  getRegularUsers,
  changeRegularUserStatus,
} from "services/usersServices";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import SearchBar from "../../Components/SearchBar";
import { useDebouncedSearch } from "../../../../hooks/useDebouncedSearch";
import AvatarWithLoader from "../../Components/Avatar/AvatarWithLoader";

export default function RegularUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const { searchTerm, debouncedSearchTerm, handleSearchChange } =
    useDebouncedSearch(500);

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      // Handle search submission if needed
      console.log("Search submitted:", searchTerm);
    }
  };

  const tableData = {
    columns: [
      { Header: "Name", accessor: "name", align: "left" },
      { Header: "Bio", accessor: "bio", align: "left" },
      { Header: "Action", accessor: "action", align: "left" },
    ],
    rows:
      users?.map((user) => ({
        name: (
          <Box display="flex" alignItems="center">
            <AvatarWithLoader src={user?.avatar} alt={user?.name} />
            <Typography variant="h6" fontWeight="bold">
              {user?.name}
            </Typography>
          </Box>
        ),
        bio: (
          <Typography variant="h6" color="text.secondary">
            {user?.bio.length > 80 ? user?.bio.slice(0, 80) + "..." : user?.bio}
          </Typography>
        ),
        action:
          loadingStatus === user._id ? (
            <CircularProgress size={20} />
          ) : (
            <ArgonBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              color="orange"
              onClick={() => {
                handleChangeUserStatus(user._id, user.status);
              }}
              sx={{ cursor: "pointer" }}
            >
              {user?.status === "active" ? (
                <BlockIcon sx={{ color: "orange" }} />
              ) : (
                <CheckBoxIcon sx={{ color: "green" }} />
              )}
              <Typography
                variant="h6"
                color={user?.status === "active" ? "orange" : "green"}
                fontWeight="bold"
              >
                {user?.status === "active" ? "Suspend" : "Activate"}
              </Typography>
            </ArgonBox>
          ),
      })) || [],
  };

  // Fetch users when debounced search term changes
  useEffect(() => {
    setLoading(true);
    getRegularUsers(debouncedSearchTerm || null)
      .then((res) => {
        setUsers(res.data);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        toast.error("Failed to fetch regular users");
        setLoading(false);
      });
  }, [debouncedSearchTerm]);

  const handleChangeUserStatus = (id, status) => {
    setLoadingStatus(id);
    changeRegularUserStatus(id, status === "active" ? "inactive" : "active")
      .then((res) => {
        if (res.success) {
          setUsers((prev) =>
            prev.map((user) =>
              user._id === id
                ? {
                    ...user,
                    status: status === "active" ? "inactive" : "active",
                  }
                : user
            )
          );
          toast.info(
            status === "active"
              ? "User suspended successfully"
              : "User activated successfully"
          );
        } else {
          toast.error(
            status === "active"
              ? "Failed to suspend user"
              : "Failed to activate user"
          );
        }
      })
      .catch((err) => {
        toast.error(
          status === "active"
            ? "Failed to suspend user"
            : "Failed to activate user"
        );
      })
      .finally(() => {
        setLoadingStatus(false);
      });
  };

  return (
    <ArgonBox>
      {/* Search Bar rendered via portal */}
      <SearchBar
        value={searchTerm}
        onChange={handleSearchChange}
        onKeyDown={handleSearchKeyDown}
        placeholder="Search regular users"
      />

      <Card sx={{ overflow: "visible", mb: 10, mt: 5, padding: 3 }}>
        <ArgonBox p={3}>
          <ArgonTypography variant="h5" fontWeight="medium">
            Regular Users
          </ArgonTypography>
        </ArgonBox>
        <ArgonBox>
          {loading ? (
            <ArgonBox display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </ArgonBox>
          ) : users.length > 0 ? (
            <DataTable
              table={{ columns: tableData.columns, rows: tableData.rows }}
              entriesPerPage={false}
              canSearch={false}
              showTotalEntries={false}
              pagination={false}
            />
          ) : (
            <ArgonBox display="flex" justifyContent="center" p={3}>
              <ArgonTypography variant="h6">No users found</ArgonTypography>
            </ArgonBox>
          )}
        </ArgonBox>
      </Card>
    </ArgonBox>
  );
}
